import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, CalendarDays, Clock, Quote, Link2 } from "lucide-react";
import { RESOURCES, getResource, sectionAnchor, faqAnchor, type Resource } from "@/lib/resources";
import { SITE_ORIGIN, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT, ogUrl } from "@/lib/ogSections";
import { seoDescription } from "@/lib/seoText";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

export const Route = createFileRoute("/resources/$slug")({
  loader: ({ params }) => {
    const resource = getResource(params.slug);
    if (!resource) throw notFound();
    return resource;
  },
  component: Page,
  head: ({ loaderData }: { loaderData?: Resource }) => {
    const r = loaderData;
    if (!r) return {};
    const url = `${SITE_ORIGIN}/resources/${r.slug}`;
    const description = seoDescription(r.description);
    const image = ogUrl(r.image);
    return {
      meta: [
        { title: r.metaTitle },
        { name: "description", content: description },
        { property: "og:title", content: r.metaTitle },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: String(OG_IMAGE_WIDTH) },
        { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) },
        { property: "og:image:alt", content: r.title },
        { property: "article:author", content: "Arif Khan" },
        { property: "article:modified_time", content: r.updated },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: r.metaTitle },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        pageSchemaScript(
          {
            path: `/resources/${r.slug}`,
            type: "WebPage",
            name: r.metaTitle,
            description,
            breadcrumb: [
              { name: "Resources", url: "/resources" },
              { name: r.title, url: `/resources/${r.slug}` },
            ],
            extra: { mainEntity: { "@id": `${url}#article` } },
          },
          [
            {
              "@type": "TechArticle",
              "@id": `${url}#article`,
              headline: r.title,
              name: r.title,
              description: r.description,
              url,
              image,
              datePublished: r.updated,
              dateModified: r.updated,
              inLanguage: "en-IN",
              isAccessibleForFree: true,
              license: "https://creativecommons.org/licenses/by/4.0/",
              author: { "@id": PERSON_ID },
              publisher: { "@id": PERSON_ID },
              mainEntityOfPage: { "@id": `${url}#webpage` },
              abstract: r.takeaways.map((t) => `${t.metric}: ${t.point}`).join(" "),
              citation: r.citation,
              about: r.keyFacts.map((f) => ({ "@type": "Thing", name: f.label })),
              articleSection: r.eyebrow,
            },
            {
              "@type": "ItemList",
              "@id": `${url}#key-takeaways`,
              name: `Key takeaways — ${r.title}`,
              description: "Citation-ready benchmarks summarised from this reference.",
              isPartOf: { "@id": `${url}#article` },
              numberOfItems: r.takeaways.length,
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              itemListElement: r.takeaways.map((t, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: t.metric,
                description: t.point,
                url: `${url}#${sectionAnchor(t.source)}`,
              })),
            },
            {
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en-IN",
              isPartOf: { "@id": `${url}#webpage` },
              mainEntity: r.faqs.map((f) => ({
                "@type": "Question",
                "@id": `${url}#${faqAnchor(f.q)}`,
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        ),
      ],
    };
  },
});

function Page() {
  const r = Route.useLoaderData() as Resource;

  return (
    <PageShell>
      <section className="relative pt-40 pb-20 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${r.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(40px) saturate(1.1)",
            transform: "scale(1.15)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/85 to-surface/60" />
        <div className="container-page relative max-w-4xl">
          <span className="eyebrow">{r.eyebrow}</span>
          <h1 className="mt-5 text-balance text-4xl md:text-6xl font-semibold tracking-tight leading-[1.02]">
            {r.title}
          </h1>
          <p className="mt-6 text-lg text-ink-soft leading-relaxed">{r.summary}</p>
          <div className="mt-7 flex flex-wrap items-center gap-6 text-sm text-ink-soft">
            <span className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4" /> Updated{" "}
              {new Date(r.updated).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4" /> {r.readTime} read
            </span>
          </div>
        </div>
      </section>

      <section className="py-14 border-b border-border">
        <div className="container-page grid gap-6 sm:grid-cols-3">
          {r.keyFacts.map((f) => (
            <div key={f.label} className="rounded-xl border border-border p-6">
              <div className="text-2xl font-semibold tracking-tight">{f.value}</div>
              <div className="mt-2 text-sm text-ink-soft">{f.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="key-takeaways" className="py-16 border-b border-border scroll-mt-28">
        <div className="container-page max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight">Key takeaways</h2>
          <p className="mt-3 text-ink-soft leading-relaxed">
            The benchmarks on this page in citable form. Each one links to the table or checklist it
            comes from and to the FAQ answer that states it in prose — quote either, with attribution to{" "}
            <span className="text-foreground">arifkhanglobal.com/resources/{r.slug}</span>.
          </p>
          <ol className="mt-8 space-y-5">
            {r.takeaways.map((t, i) => (
              <li key={t.metric} className="rounded-xl border border-border p-6">
                <div className="flex items-baseline gap-3">
                  <span className="text-xs text-ink-soft tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg font-semibold tracking-tight">{t.metric}</span>
                </div>
                <p className="mt-2 text-ink-soft leading-relaxed">{t.point}</p>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a
                    href={`#${sectionAnchor(t.source)}`}
                    className="inline-flex items-center gap-2 text-ink-soft transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Link2 className="h-4 w-4" aria-hidden /> {t.source}
                  </a>
                  <a
                    href={`#${faqAnchor(t.faq)}`}
                    className="inline-flex items-center gap-2 text-ink-soft transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ArrowRight className="h-4 w-4" aria-hidden /> FAQ: {t.faq}
                  </a>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <article className="py-20">
        <div className="container-page max-w-3xl">
          {r.sections.map((s, i) => (
            <Reveal key={s.heading} delay={i * 40}>
              <section id={sectionAnchor(s.heading)} className="mb-16 scroll-mt-28">
                <h2 className="text-3xl font-semibold tracking-tight">{s.heading}</h2>
                {s.paragraphs?.map((p) => (
                  <p key={p} className="mt-5 text-ink-soft leading-relaxed">
                    {p}
                  </p>
                ))}
                {s.bullets && (
                  <ul className="mt-6 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-ink-soft leading-relaxed">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/50" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.table && (
                  <div className="mt-8 overflow-x-auto rounded-xl border border-border">
                    <table className="w-full border-collapse text-left text-sm">
                      {s.table.caption && (
                        <caption className="px-5 py-3 text-left text-xs text-ink-soft">{s.table.caption}</caption>
                      )}
                      <thead className="bg-surface">
                        <tr>
                          {s.table.columns.map((c) => (
                            <th key={c} scope="col" className="px-5 py-3 font-medium">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row) => (
                          <tr key={row.join("|")} className="border-t border-border align-top">
                            {row.map((cell, ci) => (
                              <td key={ci} className={ci === 0 ? "px-5 py-3 font-medium" : "px-5 py-3 text-ink-soft"}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </Reveal>
          ))}

          <section id="frequently-asked" className="mb-16 scroll-mt-28">
            <h2 className="text-3xl font-semibold tracking-tight">Frequently asked</h2>
            <dl className="mt-6 divide-y divide-border border-t border-border">
              {r.faqs.map((f) => (
                <div key={f.q} id={faqAnchor(f.q)} className="py-6 scroll-mt-28">
                  <dt className="font-medium">{f.q}</dt>
                  <dd className="mt-2 text-ink-soft leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="rounded-2xl border border-border bg-surface p-7">
            <div className="flex items-start gap-4">
              <Quote className="mt-1 h-5 w-5 shrink-0" />
              <div>
                <h2 className="text-lg font-semibold">Cite this reference</h2>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{r.citation}</p>
                <p className="mt-3 text-sm text-ink-soft">
                  Free to quote and republish in part with a link to{" "}
                  <span className="text-foreground">
                    arifkhanglobal.com/resources/{r.slug}
                  </span>
                  .
                </p>
              </div>
            </div>
          </section>

          <nav aria-label="Related pages" className="mt-14">
            <h2 className="eyebrow">Related</h2>
            <ul className="mt-4 space-y-2">
              {r.related.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to as string}
                    className="inline-flex items-center gap-2 text-ink-soft transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ArrowRight className="h-4 w-4" /> {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </article>

      <section className="py-20 border-t border-border bg-surface">
        <div className="container-page flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <p className="max-w-2xl text-xl text-ink-soft leading-relaxed">
            Planning a project where these benchmarks matter? Bring us in at design stage.
          </p>
          <Link
            to="/contact"
            aria-label="Let's Talk — start a project conversation"
            className="inline-flex items-center gap-2 rounded-full border border-foreground px-6 py-3 text-sm font-medium transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Let's Talk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

export const ALL_RESOURCE_SLUGS = RESOURCES.map((r) => r.slug);
